import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const SOURCE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'circleone', label: 'Circle One' },
    { value: 'mml', label: 'MML' },
    { value: 'somocares', label: 'Somo Cares' },
];

const InviteUser = () => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');
    const [source, setSource] = useState('');
    const [forceReset, setForceReset] = useState(false);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    useEffect(() => {
        const validateRole = async () => {
            const user = await authManager.validateUser();
            const validatedRole = user?.role || null;
            setCurrentUserRole(validatedRole);
            // Lock source based on the caller's own source field
            if (validatedRole === 'superadmin') {
                setSource('');
            } else if (validatedRole === 'MML Admin' || user?.source === 'mml') {
                setSource('mml');
            } else if (user?.source === 'somocares') {
                setSource('somocares');
            } else {
                setSource(user?.source || 'circleone');
            }
        };
        validateRole();
    }, []);

    const isSuperAdmin = currentUserRole === 'superadmin';
    const isMMLAdmin = currentUserRole === 'MML Admin';
    const isCircleAdmin = currentUserRole === 'admin';

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) { toast.warning("Email is required!"); return; }
        if (!role) { toast.warning("Role is required!"); return; }
        if (role !== 'superadmin' && !source) { toast.warning("Source is required!"); return; }

        try {
            const response = await authManager.authenticatedFetch(`${Base_Url}/users/invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    role,
                    source,
                    ...(forceReset && { forceReset: true })
                })
            });

            if (response.ok) {
                toast.success("User invited successfully!");
                setEmail('');
                setRole('');
                if (isSuperAdmin) setSource('');
                setForceReset(false);
            } else {
                const data = await response.json();
                const message = data?.error?.errors || data?.error?.message || "Failed to invite user";
                toast.error(message);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error("Invite error:", error);
            toast.error("Error inviting user. Please try again.");
        }
    };

    return (
        <div>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Invite User(s)</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/users"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    <form className="form-horizontal" onSubmit={handleSubmit}>
                                        <div className="card-body">

                                            {/* Email */}
                                            <div className="form-group row">
                                                <label className="col-12 col-md-4 col-form-label">Email<span className="f_red">*</span></label>
                                                <div className="col-12 col-md-8">
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            {/* Role */}
                                            <div className="form-group row">
                                                <label className="col-12 col-md-4 col-form-label">Role<span className="f_red">*</span></label>
                                                <div className="col-12 col-md-8">
                                                    <select
                                                        className="form-control"
                                                        value={role}
                                                        onChange={(e) => setRole(e.target.value)}
                                                    >
                                                        <option value="" disabled>Select</option>
                                                        {isSuperAdmin && <option value="superadmin">Super Admin</option>}
                                                        <option value="admin">Admin</option>
                                                        <option value="user">User</option>
                                                    </select>
                                                    {role === 'superadmin' && (
                                                        <small className="text-muted mt-1 d-block">Full access — no source restriction</small>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Source — hidden when inviting a superadmin (full access, no scope needed) */}
                                            {role !== 'superadmin' && (
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-4 col-form-label">Source<span className="f_red">*</span></label>
                                                    <div className="col-12 col-md-8 d-flex align-items-center flex-wrap" style={{ gap: '16px', paddingTop: '6px' }}>
                                                        {isSuperAdmin ? (
                                                            SOURCE_OPTIONS.map(o => (
                                                                <div className="form-check" key={o.value}>
                                                                    <input
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        id={`source_${o.value}`}
                                                                        name="source"
                                                                        value={o.value}
                                                                        checked={source === o.value}
                                                                        onChange={() => setSource(o.value)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`source_${o.value}`}>
                                                                        {o.label}
                                                                    </label>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span className="form-control-plaintext fw-semibold text-capitalize">
                                                                {source === 'mml' ? 'MML' : source === 'somocares' ? 'Somo Cares' : 'Circle One'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Force Reset */}
                                            <div className="form-group row">
                                                <label className="col-12 col-md-4 col-form-label">Force Reset</label>
                                                <div className="col-12 col-md-8 d-flex align-items-center">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input mt-0 me-2"
                                                        id="forceReset"
                                                        checked={forceReset}
                                                        onChange={(e) => setForceReset(e.target.checked)}
                                                    />
                                                    <label className="form-check-label text-muted small" htmlFor="forceReset">
                                                        Re-invite an active user and reset their access
                                                    </label>
                                                </div>
                                            </div>

                                        </div>
                                        <div className="card-footer text-center">
                                            <button type="submit" className="btn btn-blue">Invite</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteUser;
